import React, { useCallback, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import BodyText from 'LuxKit/Typography/BodyText'

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
  margin-bottom: ${rem(-8)};
`

const messages = defineMessages({
  allDay: {
    id: 'r04AO22DU8g9ldLmzeyF',
    defaultMessage: 'All day',
    description: 'Time slot filter option for experiences with no specific start time',
  },
  morning: {
    id: 'QG2XM-mi9lejKz3_md6R',
    defaultMessage: 'Morning (6am–12pm)',
    description: 'Time slot filter option for experiences starting in the morning',
  },
  afternoon: {
    id: 'pPyZGRpHaRBFzGE_UzfA',
    defaultMessage: 'Afternoon (12pm–6pm)',
    description: 'Time slot filter option for experiences starting in the afternoon',
  },
  evening: {
    id: 'yW1nHz16fgyJSenhi45Q',
    defaultMessage: 'Evening (6pm+)',
    description: 'Time slot filter option for experiences starting in the evening',
  },
})

const TIME_SLOT_OPTIONS: Array<{
  value: Experiences.TimeSlotFilter;
  messageKey: keyof typeof messages;
}> = [
  { value: 'all-day', messageKey: 'allDay' },
  { value: 'morning', messageKey: 'morning' },
  { value: 'afternoon', messageKey: 'afternoon' },
  { value: 'evening', messageKey: 'evening' },
]

interface Props {
  timeSlotCounts?: Partial<Record<Experiences.TimeSlotFilter, number>>;
}

function ExperienceSearchStartTimeFilter({ timeSlotCounts }: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()

  const selectedSlots = useMemo(
    () => searchFilters.timeSlots ?? [],
    [searchFilters.timeSlots],
  )

  const onToggle = useCallback(
    (value: Experiences.TimeSlotFilter) => {
      const next = selectedSlots.includes(value)
        ? selectedSlots.filter((v) => v !== value)
        : [...selectedSlots, value]
      setSearchFilters({
        ...searchFilters,
        timeSlots: next.length > 0 ? next : undefined,
      })
    },
    [selectedSlots, searchFilters, setSearchFilters],
  )

  return (
    <ListContainer>
      {TIME_SLOT_OPTIONS.map((option) => (
        <CheckboxInputListItem
          key={option.value}
          inputPlacement="start"
          checked={selectedSlots.includes(option.value)}
          onChange={() => onToggle(option.value)}
          title={intl.formatMessage(messages[option.messageKey])}
          size="medium"
          disabled={timeSlotCounts?.[option.value] === 0}
        >
          {timeSlotCounts?.[option.value] !== undefined && (
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'b2g6ruwcEWLYcBguptYb',
                  defaultMessage: '({count})',
                  description: 'Count of matching experiences shown next to a time slot filter option',
                },
                { count: timeSlotCounts[option.value] },
              )}
            </BodyText>
          )}
        </CheckboxInputListItem>
      ))}
    </ListContainer>
  )
}

export default ExperienceSearchStartTimeFilter
